import React, { useEffect, useState, useRef } from "react";
import Question from "../Question/Question";
import GameOver from "../GameOver/GameOver";
import "./App.css";
import {
  staticRepositoriesDB,
  RepositoryType,
  fetchRepositories,
} from "../ReposDB";
import { shuffle } from "lodash";

interface OptionsType {
  name: string;
  description: string;
  hint: string;
  colour: string;
  icon: string;
}

let repositoriesDB: RepositoryType[] = [];

// The order followed to pick the repositories for questions
let order: number[] = [];

function generateOrder(n: number): number[] {
  // Generate an array of numbers from 0 to n-1 in shufled order
  const orderedArray = Array.from({ length: n }, (_, index) => index);

  const shuffledArray = shuffle(orderedArray);

  return shuffledArray;
}

const App: React.FC = () => {
  const [questionTitle] = useState(
    "Which has more Pull Requests open?",
  );
  const [options, setOptions] = useState<OptionsType[]>([]);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  const nextRepoId = useRef(0);

  useEffect(() => {
    const init = (): void => {
      const firstRepo = getNextRepo();
      const secondRepo = getNextRepo();
      setOptions([
        {
          name: firstRepo.name,
          description: firstRepo.description,
          hint: "PRs: " + firstRepo.open_prs,
          colour: firstRepo.colour,
          icon: firstRepo.icon,
        },
        {
          name: secondRepo.name,
          description: secondRepo.description,
          hint: "PRs: " + secondRepo.open_prs,
          colour: secondRepo.colour,
          icon: secondRepo.icon,
        },
      ]);
    };

    const loadRepositories = async () => {
      try {
        const data = await fetchRepositories();
        repositoriesDB = data;
      } catch (error) {
        repositoriesDB = staticRepositoriesDB;
      } finally {
        order = generateOrder(repositoriesDB.length);
        init();
      }
    };

    if (repositoriesDB.length === 0) {
      loadRepositories();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRepoByName = (currentAnswer: string) => {
    for (let i = 0; i < repositoriesDB.length; i++) {
      if (repositoriesDB[i].name === currentAnswer) {
        return repositoriesDB[i];
      }
    }
    return null;
  };

  const getRepoByOption = (option: OptionsType) => {
    return getRepoByName(option.name);
  };

  const getNextRepo = (): RepositoryType => {
    const id = nextRepoId.current;
    nextRepoId.current++;
    if (nextRepoId.current >= order.length) {
      nextRepoId.current = 0;
    }
    return repositoriesDB[order[id]];
  };

  const getNextOption = (): OptionsType => {
    const nextRepo = getNextRepo();
    return {
      name: nextRepo.name,
      description: nextRepo.description,
      hint: "PRs: " + nextRepo.open_prs,
      colour: nextRepo.colour,
      icon: nextRepo.icon
    };
  };

  const handleRestart = () => {
    setGameOver(false);
    setScore(0);
  };

  const resolveQuestion = (): OptionsType | undefined => {
    if (options.length === 0) {
      return;
    }

    const repo0 = getRepoByOption(options[0]);
    const repo1 = getRepoByOption(options[1]);

    if (repo0 === null || repo1 === null) {
      return;
    }

    let answer = options[0];
    if (repo0.open_prs < repo1.open_prs) {
      answer = options[1];
    }
    return answer;
  };

  const goToGameOver = (): void => {
    setGameOver(true);
  };

  const increaseScore = (): void => {
    setScore(score + 1);
  };

  return (
    <div className="App">
      {options.length === 0 ? (
        <div>Loading</div>
      ) : !gameOver ? (
        <Question
          questionTitle={questionTitle}
          options={options}
          resolveQuestion={resolveQuestion}
          getNextOption={getNextOption}
          goToGameOver={goToGameOver}
          increaseScore={increaseScore}
        />
      ) : (
        <GameOver score={score} handleRestart={handleRestart} />
      )}
    </div>
  );
};

export default App;
